import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, CircleCheck as CheckCircle2, Circle as XCircle, Clock, CalendarX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { proposalService, type Proposal } from '@/lib/proposalService';
import { ProposalRenderer } from '@/components/proposals/ProposalRenderer';
import { SignatureModal } from '@/components/proposals/SignatureModal';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useAuth } from '@/context/AuthContext';
import { exportElementToPdf } from '@/lib/proposalPdf';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';

export default function PortalProposalView() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const { user, profile } = useAuth();
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [sender, setSender] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [declining, setDeclining] = useState(false);
  const docRef = useRef<HTMLElement>(null);
  const openedRecorded = useRef(false);

  useEffect(() => {
    if (!slug) return;
    proposalService.getProposalBySlug(slug).then(async p => {
      if (p) {
        const { supabase } = await import('@/lib/supabase');
        const { data: senderProfile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', p.created_by)
          .maybeSingle();
        setSender(senderProfile);
      }
      setProposal(p);
      setLoading(false);
      if (p && user && p.client_user_id === user.id && !openedRecorded.current) {
        openedRecorded.current = true;
        proposalService.recordOpen(p.id).catch(() => {});
      }
    }).catch(() => setLoading(false));
  }, [slug, user?.id]);

  const downloadPdf = async () => {
    if (!docRef.current || !proposal) return;
    toast.info('Generating PDF...');
    try {
      await exportElementToPdf(docRef.current, proposal.title || 'proposal');
      await proposalService.recordEvent(proposal.id, 'downloaded', { actor: 'client' });
    } catch {
      toast.error('Failed to export PDF');
    }
  };

  const handleApprove = async (name: string, signatureData: string) => {
    if (!proposal) return;
    try {
      const updated = await proposalService.approveProposal(proposal.id, name, signatureData);
      setProposal(updated);
      toast.success('Proposal approved and signed');
    } catch (err) {
      console.error(err);
      toast.error('Failed to approve proposal');
    }
  };

  const handleDecline = async () => {
    if (!proposal) return;
    setDeclining(true);
    try {
      const updated = await proposalService.declineProposal(proposal.id, declineReason.trim());
      setProposal(updated);
      setDeclineOpen(false);
      toast.success('Proposal declined');
    } catch {
      toast.error('Failed to decline proposal');
    } finally {
      setDeclining(false);
    }
  };

  if (loading) return <div className="py-20 text-center text-muted-foreground">Loading...</div>;
  if (!proposal) return <div className="py-20 text-center text-muted-foreground">Proposal not found</div>;

  const appearance = currentWorkspace?.appearance;
  const agencyLogo = appearance?.logo || currentWorkspace?.logo;
  const accent = appearance?.theme?.accentColor || '#3B82F6';
  const isActionable = proposal.status === 'pending';
  const defaultName = profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : '';

  return (
    <div className="min-h-screen bg-muted/30 pb-32">
      <Helmet><title>{proposal.title} | Proposal</title></Helmet>

      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border">
        <div className="w-full px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/proposals')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{proposal.title}</div>
              <ProposalStatusBadge proposal={proposal} />
            </div>
          </div>
          <Button size="sm" variant="outline" className="gap-2" onClick={downloadPdf}>
            <Download className="w-4 h-4" /> Download PDF
          </Button>
        </div>
      </div>

      <div className="w-full px-4 sm:px-6 py-8">
        <ProposalRenderer
          ref={docRef}
          proposal={proposal}
          agencyName={currentWorkspace?.name}
          agencyLogo={agencyLogo}
          sender={sender}
        />
      </div>

      {isActionable && (
        <div className="fixed bottom-0 left-0 md:left-64 right-0 z-30 bg-background/95 backdrop-blur border-t border-border">
          <div className="w-full px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
            <div className="text-sm">
              <div className="font-semibold text-foreground">Ready to proceed?</div>
              <div className="text-xs text-muted-foreground">
                {proposal.valid_until
                  ? `Offer valid until ${format(new Date(proposal.valid_until), 'PPP')}`
                  : 'Review the proposal, then approve or decline below'}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" className="gap-2" onClick={() => setDeclineOpen(true)}>
                <XCircle className="w-4 h-4" /> Decline
              </Button>
              <Button
                className="gap-2 text-white"
                style={{ backgroundColor: accent }}
                onClick={() => setSignatureOpen(true)}
              >
                <CheckCircle2 className="w-4 h-4" /> Approve & Sign
              </Button>
            </div>
          </div>
        </div>
      )}

      <SignatureModal
        open={signatureOpen}
        onOpenChange={setSignatureOpen}
        onSubmit={handleApprove}
        defaultName={defaultName}
      />

      <Dialog open={declineOpen} onOpenChange={setDeclineOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Decline proposal</DialogTitle>
            <DialogDescription>
              Let your agency know why this proposal isn't a fit. This helps them respond quickly.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            placeholder="Share your reason..."
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeclineOpen(false)} disabled={declining}>Cancel</Button>
            <Button variant="destructive" onClick={handleDecline} disabled={declining || !declineReason.trim()}>
              {declining ? 'Declining...' : 'Decline proposal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const ProposalStatusBadge: React.FC<{ proposal: Proposal }> = ({ proposal }) => {
  const { status } = proposal;
  const base = 'inline-flex items-center gap-1 text-[11px] font-medium';
  if (status === 'pending') {
    return (
      <span className={cn(base, 'text-amber-700 dark:text-amber-400')}>
        <Clock className="w-3 h-3" />
        Awaiting your review
        {proposal.valid_until && <span className="text-muted-foreground ml-1">· expires {formatDistanceToNow(new Date(proposal.valid_until), { addSuffix: true })}</span>}
      </span>
    );
  }
  if (status === 'approved') return <span className={cn(base, 'text-emerald-700 dark:text-emerald-400')}><CheckCircle2 className="w-3 h-3" /> Approved</span>;
  if (status === 'declined') return <span className={cn(base, 'text-rose-700 dark:text-rose-400')}><XCircle className="w-3 h-3" /> Declined</span>;
  if (status === 'expired') return <span className={cn(base, 'text-slate-600 dark:text-slate-400')}><CalendarX className="w-3 h-3" /> Expired</span>;
  return null;
};
